import { Grid, Flex, FlexItem, Link, Button, Alert, Box } from '@gusto/workbench';
import {
  Form,
  SubmitButton,
  TextField,
  SelectField,
  Option,
  Checkbox,
  CheckboxGroup,
} from '@gusto/workbench-formik';
import FormattedPhoneField from 'packages/signup/components/signupForm/formattedPhoneField';
import { useRef } from 'react';
import numberCompaniesBucket from './companies_bucket_values';
import getSHA256Hash from './partner_form_details_helpers';

const PartnerFormDetails = ({
  oktaSSOEnabled,
  ssoDomains,
  userInOktaSSO,
  setUserInOktaSSO,
}: {
  oktaSSOEnabled: boolean;
  ssoDomains: string[];
  userInOktaSSO: boolean;
  setUserInOktaSSO: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const lastRequestRef = useRef(0);

  const checkEmailDomain = async (email: string) => {
    const reqId = lastRequestRef.current + 1;
    lastRequestRef.current = reqId;
    const emailDomain = email.substring(email.indexOf('@') + 1).toLowerCase();
    const domainHash = await getSHA256Hash(emailDomain);
    if (reqId !== lastRequestRef.current) return; // stale
    setUserInOktaSSO(oktaSSOEnabled && ssoDomains.includes(domainHash));
  };

  return (
    <Grid component={Form} marginBottom={8} rowGap={3}>
      <Flex columnGap={3} rowGap={3} flexDirection="column" flexDirectionMd="row">
        <FlexItem width={1} widthMd={1 / 2}>
          <TextField name="accountant_lead.first_name" type="text" label="First name" required />
        </FlexItem>
        <FlexItem width={1} widthMd={1 / 2}>
          <TextField name="accountant_lead.last_name" type="text" label="Last name" required />
        </FlexItem>
      </Flex>
      <TextField
        name="accountant_lead.email"
        type="email"
        label="Business email address"
        onChange={e => {
          const { value } = e.target;
          if (oktaSSOEnabled && value && value.includes('@', 1)) {
            checkEmailDomain(value);
          } else if (oktaSSOEnabled && userInOktaSSO) {
            setUserInOktaSSO(false);
          }
        }}
        required
      />
      {(!userInOktaSSO || !oktaSSOEnabled) && (
        <>
          <TextField
            name="accountant_lead.accounting_firm_name"
            type="text"
            label="Firm name"
            required
          />
          <SelectField
            name="accountant_lead.number_companies_bucket"
            label="No. of business clients"
            required
          >
            <Option value="" />
            {numberCompaniesBucket.map(opt => (
              <Option key={opt.label} value={opt.value}>
                {opt.label}
              </Option>
            ))}
          </SelectField>
          <SelectField
            name="accountant_lead.number_payroll_services_clients_bucket"
            label="No. of payroll services clients"
            required
          >
            <Option value="" />
            {numberCompaniesBucket.map(opt => (
              <Option key={opt.label} value={opt.value}>
                {opt.label}
              </Option>
            ))}
          </SelectField>
          <TextField name="accountant_lead.website" type="text" label="Website" />
          <FormattedPhoneField
            name="accountant_lead.phone"
            label="Phone number"
            type="tel"
            placeholder="(555) 012-3456"
            maxLength={14}
            required
          />
        </>
      )}
      {!oktaSSOEnabled && (
        <CheckboxGroup legend=" " name="terms_acceptance">
          <Checkbox
            label={
              <>
                I accept the{' '}
                <Link href="https://gusto.com/legal/partners" target="_blank">
                  Terms of Service
                </Link>{' '}
                and{' '}
                <Link href="https://gusto.com/legal/privacy" target="_blank">
                  Privacy Policy
                </Link>
              </>
            }
          />
        </CheckboxGroup>
      )}
      {userInOktaSSO && (
        <>
          <Box role="alert" marginTop={3}>
            <Alert
              headingLevel={2}
              role="none"
              severity="info"
              label="Your firm is using Okta to manage access"
            >
              <Box component="p" margin="spacing.0">
                You won’t need a separate Gusto password. You’ll be redirected to sign in through
                Okta to get started.
              </Box>
            </Alert>
          </Box>
          <Button variant="primary" style={{ marginTop: '20px', marginBottom: '10px' }}>
            Continue with SSO
          </Button>
          <Button variant="link" type="reset">
            Return to sign up
          </Button>
        </>
      )}
      {!userInOktaSSO && (
        <SubmitButton className="btn btn-submit-form">Sign up with email</SubmitButton>
      )}
    </Grid>
  );
};

export default PartnerFormDetails;
